import { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AssignmentOutlined } from '@mui/icons-material';
import { Box, Button, Skeleton, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { getActivityPeriodSummary } from '../../../../services/activity';
import { getAlerts } from '../../../../services/alerts';
import { useContinuousData } from '../../../../hooks/useContinuousData';
import { useUser } from '../../../../hooks/useUser';
import { getContinuousHeartRateRange } from './continuousHeartRate';
import {
    CONTINUOUS_STEPS_TYPE,
    getDailyStepsAverage,
    normalizeContinuousSteps
} from './continuousSteps';
import OverviewCard from './OverviewCard';

const getPaginationCount = (response) => {
    const header = response?.headers?.['pagination-count'];
    const count = Number(header);
    if (header == null || header === '' || !Number.isSafeInteger(count) || count < 0) {
        throw new Error('Invalid alert pagination count.');
    }
    return count;
};

const getQueryState = (query) => ({
    loading: query.isPending,
    error: query.isError,
    onRetry: query.refetch
});

const getAdherence = (summary) =>
    summary?.total > 0 ? Math.round((summary.completed / summary.total) * 100) : null;

const SummaryMetric = ({
    label,
    displayValue,
    current,
    previous,
    fixedMaximum,
    positive,
    loading,
    error,
    onRetry
}) => {
    const { t } = useTranslation();
    const maximum = fixedMaximum || Math.max(current || 0, previous || 0, 1) * 1.15;
    const currentWidth = Math.min(((current || 0) / maximum) * 100, 100);
    const previousWidth = Math.min(((previous || 0) / maximum) * 100, 100);

    return (
        <Box>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    mb: 0.55
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                    <Typography
                        variant="caption"
                        noWrap
                        title={label}
                        sx={{ color: '#263238', fontWeight: 700 }}
                    >
                        {label}
                    </Typography>
                </Box>
                <Typography
                    variant="caption"
                    sx={{
                        flexShrink: 0,
                        color:
                            positive == null
                                ? '#263238'
                                : positive
                                  ? '#168A0D'
                                  : '#D32F2F',
                        fontWeight: 700
                    }}
                >
                    {loading ? <Skeleton width={30} /> : error ? '—' : displayValue}
                </Typography>
            </Box>
            {error ? (
                <Button
                    size="small"
                    onClick={() => onRetry()}
                    title={t('userPage.overview.thirtyDays.loadError')}
                    aria-label={`${label}: ${t('userPage.overview.measurements.retry')}`}
                    sx={{ p: 0, minWidth: 0, fontSize: '0.65rem' }}
                >
                    {t('userPage.overview.measurements.retry')}
                </Button>
            ) : loading ? (
                <Skeleton variant="rounded" height={6} />
            ) : (
                <Box
                    sx={{
                        position: 'relative',
                        height: 6,
                        overflow: 'hidden',
                        bgcolor: '#D9DEE2',
                        borderRadius: 1
                    }}
                >
                    <Box
                        sx={{
                            position: 'absolute',
                            inset: 0,
                            width: `${previousWidth}%`,
                            bgcolor: '#A9C3D7',
                            borderRadius: 1
                        }}
                    />
                    <Box
                        sx={{
                            position: 'absolute',
                            inset: 0,
                            width: `${currentWidth}%`,
                            bgcolor: '#0A579B',
                            borderRadius: 1
                        }}
                    />
                </Box>
            )}
        </Box>
    );
};

SummaryMetric.propTypes = {
    label: PropTypes.string.isRequired,
    displayValue: PropTypes.node.isRequired,
    current: PropTypes.number,
    previous: PropTypes.number,
    fixedMaximum: PropTypes.number,
    positive: PropTypes.bool,
    loading: PropTypes.bool,
    error: PropTypes.bool,
    onRetry: PropTypes.func
};

const ThirtyDaySummaryCard = ({ patientUuid }) => {
    const { t, i18n } = useTranslation();
    const { user } = useUser();
    const queryClient = useQueryClient();
    const zone = user.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
    const range = getContinuousHeartRateRange({ period: '30d', zone });
    const currentStartKey = dayjs(range.startTimestamp).tz(zone).format('YYYY-MM-DD');
    const previousStartKey = dayjs
        .utc(currentStartKey)
        .subtract(30, 'day')
        .format('YYYY-MM-DD');
    const endDateKey = dayjs(range.endTimestamp).tz(zone).format('YYYY-MM-DD');
    const previousStartTimestamp = dayjs.tz(previousStartKey, zone).toISOString();
    const locale = i18n.resolvedLanguage?.startsWith('pt') ? 'pt-PT' : 'en-GB';

    const adherenceQuery = useQuery({
        queryKey: [
            'patient-thirty-day-summary',
            patientUuid,
            previousStartKey,
            currentStartKey,
            endDateKey,
            zone
        ],
        queryFn: async () => {
            const [currentActivity, previousActivity] = await Promise.all([
                getActivityPeriodSummary({
                    queryClient,
                    patientUuid,
                    startDate: currentStartKey,
                    endDate: dayjs()
                }),
                getActivityPeriodSummary({
                    queryClient,
                    patientUuid,
                    startDate: previousStartKey,
                    endDate: currentStartKey
                })
            ]);

            return {
                currentAdherence: getAdherence(currentActivity),
                previousAdherence: getAdherence(previousActivity)
            };
        },
        enabled: Boolean(patientUuid),
        staleTime: 60 * 1000,
        refetchOnMount: 'always'
    });

    const alertsQuery = useQuery({
        queryKey: [
            'patient-alerts-thirty-day-summary',
            patientUuid,
            previousStartKey,
            currentStartKey,
            endDateKey,
            zone
        ],
        queryFn: async () => {
            const [current, previous] = await Promise.all([
                getAlerts({
                    patient: patientUuid,
                    qi: currentStartKey,
                    df: endDateKey,
                    pageLimit: 1
                }),
                getAlerts({
                    patient: patientUuid,
                    qi: previousStartKey,
                    df: currentStartKey,
                    pageLimit: 1
                })
            ]);
            return {
                current: getPaginationCount(current),
                previous: getPaginationCount(previous)
            };
        },
        enabled: Boolean(patientUuid),
        staleTime: 60 * 1000,
        refetchOnMount: 'always'
    });
    // Fetch both comparison periods together, including later uploads for earlier days.
    const stepsQuery = useContinuousData({
        patientUuid,
        dtype: CONTINUOUS_STEPS_TYPE,
        startTimestamp: previousStartTimestamp,
        endTimestamp: range.endTimestamp
    });
    const steps = useMemo(() => {
        const days = normalizeContinuousSteps(stepsQuery.data, {
            zone,
            startTimestamp: previousStartTimestamp,
            endTimestamp: range.endTimestamp
        });
        return {
            current: getDailyStepsAverage(
                days.filter((day) => day.date >= currentStartKey)
            ),
            previous: getDailyStepsAverage(
                days.filter((day) => day.date < currentStartKey)
            )
        };
    }, [
        stepsQuery.data,
        zone,
        previousStartTimestamp,
        range.endTimestamp,
        currentStartKey
    ]);
    const data = adherenceQuery.data;
    const metrics = [
        {
            key: 'adherence',
            label: t('userPage.overview.thirtyDays.adherence'),
            displayValue:
                data?.currentAdherence == null ? '—' : `${data.currentAdherence}%`,
            current: data?.currentAdherence,
            previous: data?.previousAdherence,
            fixedMaximum: 100,
            ...getQueryState(adherenceQuery)
        },
        {
            key: 'resting-heart-rate',
            label: t('userPage.overview.thirtyDays.restingHeartRate'),
            displayValue: t('userPage.overview.thirtyDays.noData')
        },
        {
            key: 'recovery-heart-rate',
            label: t('userPage.overview.thirtyDays.recoveryHeartRate'),
            displayValue: t('userPage.overview.thirtyDays.noData')
        },
        {
            key: 'steps',
            label: t('userPage.overview.thirtyDays.dailySteps'),
            displayValue:
                steps.current == null
                    ? t('userPage.overview.thirtyDays.noData')
                    : new Intl.NumberFormat(locale).format(Math.round(steps.current)),
            current: steps.current,
            previous: steps.previous,
            ...getQueryState(stepsQuery)
        },
        {
            key: 'alerts',
            label: t('userPage.overview.thirtyDays.alerts'),
            displayValue: alertsQuery.data?.current ?? '—',
            current: alertsQuery.data?.current,
            previous: alertsQuery.data?.previous,
            ...getQueryState(alertsQuery)
        }
    ];

    return (
        <OverviewCard
            title={t('userPage.overview.lastThirtyDays')}
            icon={<AssignmentOutlined fontSize="small" />}
            minHeight={400}
        >
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateRows: `repeat(${metrics.length}, minmax(0, 1fr))`,
                    flex: 1,
                    minHeight: 0,
                    alignItems: 'center',
                    gap: 0.75,
                    py: 0.5
                }}
            >
                {metrics.map(({ key, ...metric }) => (
                    <SummaryMetric key={key} {...metric} />
                ))}
            </Box>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: 2,
                    flexShrink: 0,
                    pt: 1
                }}
            >
                {[
                    {
                        label: t('userPage.overview.thirtyDays.previous'),
                        color: '#A9C3D7'
                    },
                    {
                        label: t('userPage.overview.thirtyDays.current'),
                        color: '#0A579B'
                    }
                ].map((item) => (
                    <Box
                        key={item.label}
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}
                    >
                        <Box
                            sx={{
                                width: 8,
                                height: 8,
                                bgcolor: item.color,
                                borderRadius: '50%'
                            }}
                        />
                        <Typography
                            variant="caption"
                            sx={{ color: '#263238', fontWeight: 600 }}
                        >
                            {item.label}
                        </Typography>
                    </Box>
                ))}
            </Box>
        </OverviewCard>
    );
};

ThirtyDaySummaryCard.propTypes = {
    patientUuid: PropTypes.string.isRequired
};

export default ThirtyDaySummaryCard;
